import { Link } from 'react-router-dom';
import { TableCell, TableRow } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Pencil, Trash2 } from 'lucide-react';
import type { Expense } from '../../../types/car';
import { useLanguage } from '../../../contexts/LanguageContext';

interface ExpenseTableRowProps {
  expense: Expense;
  formatDateTime: (dateString: string) => string;
  formatPrice: (amount: number, currency?: 'IQD' | 'USD') => string;
  deleteId: number | null;
  setDeleteId: (id: number | null) => void;
  onDeleteSuccess: (id: number) => void;
}

export function ExpenseTableRow({
  expense,
  formatDateTime,
  formatPrice,
  deleteId,
  setDeleteId,
  onDeleteSuccess,
}: ExpenseTableRowProps) {
  const { t } = useLanguage();
  return (
    <TableRow className="hover:bg-slate-50">
      <TableCell className="text-sm text-slate-700 font-medium tabular-nums">#{expense.id}</TableCell>
      <TableCell className="text-sm text-slate-700 whitespace-nowrap">{formatDateTime(expense.expenseDate)}</TableCell>
      <TableCell className="text-sm text-slate-700">
        {(expense.expenseType ?? 'STORE') === 'CAR' ? (
          <span className="inline-flex items-center rounded-full bg-cyan-100 px-2.5 py-0.5 text-xs font-medium text-cyan-800">
            {t('expense.carType')}
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800">
            {t('expense.storeLabel')}
          </span>
        )}
      </TableCell>
      <TableCell className="text-sm text-slate-700">{expense.description}</TableCell>
      <TableCell className="text-sm text-slate-700">{expense.category?.name ?? '—'}</TableCell>
      <TableCell className="text-sm text-slate-700">
        {(expense.expenseType ?? 'STORE') === 'CAR' && expense.warehouseCar
          ? `#${expense.warehouseCar.id} ${expense.warehouseCar.brand?.name ?? ''} ${expense.warehouseCar.model?.name ?? ''} ${expense.warehouseCar.year ?? ''}`
          : '—'}
      </TableCell>
      <TableCell className="text-right text-sm font-medium text-red-600 tabular-nums">
        {formatPrice(expense.amount, expense.currency)}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" asChild className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 hover:text-amber-900" aria-label={t('expense.edit')}>
            <Link to={`/expenses/edit-expense/${expense.id}`}>
              <Pencil className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteId(expense.id)}
            className="bg-red-600 hover:bg-red-700"
            aria-label={t('common.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <Dialog open={deleteId === expense.id} onOpenChange={(open) => !open && setDeleteId(null)}>
          <DialogContent className="sm:max-w-md border-slate-200">
            <DialogHeader>
              <DialogTitle>{t('expense.deleteConfirm')}</DialogTitle>
              <DialogDescription>
                &quot;{expense.description}&quot; – {formatPrice(expense.amount, expense.currency)}. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteId(null)}
                className="border-slate-200"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
                onClick={() => onDeleteSuccess(expense.id)}
              >
                {t('common.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TableCell>
    </TableRow>
  );
}
